import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["badge", "count", "menu", "list"]
  static values = { unreadCount: { type: Number, default: 0 } }

  connect() {
    this.open = false
    this.clickOutside = this._clickOutside.bind(this)

    this.subscription = consumer.subscriptions.create(
      { channel: "UserNotificationsChannel" },
      {
        received: (data) => this._handleMessage(data)
      }
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    document.removeEventListener("click", this.clickOutside)
  }

  toggle() {
    this.open ? this._close() : this._open()
  }

  markAllRead() {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch("/notificaciones/marcar-leidas", {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      }
    })
      .then(res => res.json())
      .then(data => this._updateBadge(data.unread_count))
      .catch(e => console.error("[notification-bell]", e))
  }

  // ── Private ──

  _handleMessage(data) {
    if (data.type === "notification") {
      this._updateBadge(data.unread_count)
      this._prependNotification(data.notification)
    } else if (data.type === "unread_count") {
      this._updateBadge(data.unread_count)
    }
  }

  _updateBadge(count) {
    this.unreadCountValue = count
    if (this.hasCountTarget) this.countTarget.textContent = count
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.toggle("hidden", count === 0)
    }
  }

  _prependNotification(notification) {
    if (!this.hasListTarget) return

    // Remove empty state if present
    const empty = this.listTarget.querySelector("[data-empty]")
    if (empty) empty.remove()

    const html = this._buildNotificationHtml(notification)
    this.listTarget.insertAdjacentHTML("afterbegin", html)

    // Keep max 10 items
    const items = this.listTarget.querySelectorAll("[data-notification-item]")
    if (items.length > 10) items[items.length - 1].remove()
  }

  _buildNotificationHtml(n) {
    return `
      <a href="/notificaciones/${n.id}" data-notification-item
         class="flex items-start gap-3 px-4 py-3 hover:bg-surface-light transition-colors bg-slate-800/50">
        <div class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-blue-400/15 mt-0.5">
          <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <p class="text-sm font-semibold text-white truncate">${this._escapeHtml(n.title)}</p>
            <span class="shrink-0 w-2 h-2 rounded-full bg-accent"></span>
          </div>
          <p class="text-xs text-slate-400 line-clamp-2 mt-0.5">${this._escapeHtml(n.body || "")}</p>
          <p class="text-xs text-slate-500 mt-1">Ahora</p>
        </div>
      </a>
    `
  }

  _open() {
    this.menuTarget.classList.remove("hidden")
    Object.assign(this.menuTarget.style, {
      opacity: "0",
      transform: "scale(0.95)",
      transformOrigin: "top right",
      transition: "opacity 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1)"
    })
    this.menuTarget.offsetHeight
    this.menuTarget.style.opacity = "1"
    this.menuTarget.style.transform = "scale(1)"
    this.open = true
    document.addEventListener("click", this.clickOutside)
  }

  _close() {
    this.menuTarget.style.opacity = "0"
    this.menuTarget.style.transform = "scale(0.95)"
    if (this._prefersReducedMotion) {
      this.menuTarget.classList.add("hidden")
    } else {
      this.menuTarget.addEventListener("transitionend", () => {
        this.menuTarget.classList.add("hidden")
      }, { once: true })
    }
    this.open = false
    document.removeEventListener("click", this.clickOutside)
  }

  _clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._close()
    }
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  get _prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
